/**
 * What kind of failure a load ended in. The kind decides the behavior: sign in again, show
 * "no access", show "not found or no access", retry, correct the input, drop it, or stop and report.
 *
 * A failed load must carry one of these. It must never be represented as an empty result.
 */
export type ErrorKind =
    /** There is no valid session. */
    | 'unauthenticated'
    /** The session is valid and the server refused the request. */
    | 'forbidden'
    /** The thing does not exist, or the user may not know it exists. */
    | 'not_found'
    /** A network failure or a server error that a retry may fix. */
    | 'transient'
    /** A deployment or data problem that a retry will not fix. */
    | 'misconfigured'
    /** The server rejected the content of the request. The caller corrects the input; nobody retries. */
    | 'invalid'
    /** The caller canceled the request. Never a failure to show or to report. */
    | 'aborted'

export const ERROR_KINDS: readonly ErrorKind[] = [
    'unauthenticated',
    'forbidden',
    'not_found',
    'transient',
    'misconfigured',
    'invalid',
    'aborted',
]

/** Kinds worth retrying automatically with backoff. */
export function isRetryable(kind: ErrorKind): boolean {
    return kind === 'transient'
}

/** A load that did not settle within its deadline. A retry may succeed, so it is transient. */
export class AccessLoadTimeoutError extends Error {
    constructor(readonly timeoutMs: number) {
        super(`The access load did not finish within ${timeoutMs} ms`)
        this.name = 'AccessLoadTimeoutError'
    }
}

interface HttpLikeError {
    response?: { status?: number; data?: unknown }
    request?: unknown
    code?: string | number
    message?: string
    name?: string
    /** The error this one wraps. */
    cause?: unknown
    /** Set on the cancellation value the HTTP client throws for a canceled request. */
    __CANCEL__?: boolean
}

const INVALID_STATUSES: readonly number[] = [400, 409, 422]

/** The legacy numeric `DOMException.code` values, for an error that carries the code but not the name. */
const DOM_ABORT_ERR = 20
const DOM_TIMEOUT_ERR = 23
const DOM_NETWORK_ERR = 19

/** Codes the HTTP client sets when the request left but no answer came back. */
const NETWORK_CODES: readonly string[] = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ECONNREFUSED']

/** True for the value the HTTP client throws when the caller canceled, and for a DOM abort. */
const isAborted = (err: HttpLikeError): boolean =>
    err.__CANCEL__ === true || err.code === 'ERR_CANCELED' || err.name === 'AbortError' || err.code === DOM_ABORT_ERR

/** True for a DOM timeout or network error, by name or legacy code. A retry may succeed, so it is transient. */
const isDomTransient = (err: HttpLikeError): boolean =>
    err.name === 'TimeoutError' ||
    err.name === 'NetworkError' ||
    err.code === DOM_TIMEOUT_ERR ||
    err.code === DOM_NETWORK_ERR

/**
 * True when the request went out and no answer came back. A code, when present, decides: a client
 * configuration error (invalid URL, bad option) also has no response but retrying cannot fix it.
 * Without a code, a request that was sent and got no response is a network failure.
 */
const isNetworkFailure = (err: HttpLikeError): boolean =>
    err.code === undefined
        ? err.request !== undefined
        : typeof err.code === 'string' && NETWORK_CODES.includes(err.code)

/**
 * The TypeError `fetch` rejects with when the request never got an answer (offline, DNS, CORS, a
 * dropped connection). Browsers word it differently; a TypeError with any other message is a
 * programming error.
 */
const FETCH_NETWORK_FAILURE = /^(failed to fetch|load failed|networkerror when attempting to fetch|fetch failed)/i
const isFetchNetworkFailure = (error: unknown): boolean =>
    error instanceof TypeError && FETCH_NETWORK_FAILURE.test(error.message)

/**
 * Classifies a thrown value from an HTTP call. An error with no response is transient only
 * when it is a network failure (the request never got an answer); any other code is misconfigured. An error that names its own kind is that kind; a wrapper with nothing of its own is classified by its
 * cause. Anything that is not an HTTP error is misconfigured, since retrying a
 * programming error does not help.
 */
export function classifyError(error: unknown): ErrorKind {
    if (error instanceof AccessLoadTimeoutError) {
        return 'transient'
    }

    if (typeof error !== 'object' || error === null) {
        return 'misconfigured'
    }

    const ownKind = (error as { kind?: unknown }).kind
    if (ERROR_KINDS.includes(ownKind as ErrorKind)) {
        return ownKind as ErrorKind
    }

    if (isFetchNetworkFailure(error)) {
        return 'transient'
    }

    const err = error as HttpLikeError
    if (isAborted(err)) {
        return 'aborted'
    }
    if (isDomTransient(err)) {
        return 'transient'
    }

    const status = err.response?.status

    if (status === undefined) {
        if (isNetworkFailure(err)) {
            return 'transient'
        }
        if (err.code !== undefined) {
            return 'misconfigured'
        }
        return err.cause === undefined ? 'misconfigured' : classifyError(err.cause)
    }

    if (status === 401) {
        return 'unauthenticated'
    }
    if (status === 403) {
        return 'forbidden'
    }
    if (status === 404) {
        return 'not_found'
    }
    if (status === 408 || status === 429 || status >= 500) {
        return 'transient'
    }
    if (INVALID_STATUSES.includes(status)) {
        return 'invalid'
    }

    return 'misconfigured'
}

/**
 * The server's own description of why it rejected the content, when the response body carries one,
 * so the screen that sent the request can show it. Undefined for any other failure.
 */
export function invalidDetail(error: unknown): string | undefined {
    if (classifyError(error) !== 'invalid') {
        return undefined
    }

    const body = (error as HttpLikeError).response?.data
    if (typeof body === 'string') {
        return body || undefined
    }
    if (typeof body !== 'object' || body === null) {
        return undefined
    }

    const { detail, message } = body as { detail?: unknown; message?: unknown }
    const text = typeof detail === 'string' ? detail : typeof message === 'string' ? message : undefined
    return text || undefined
}
